import { HttpBody, HttpOptions, HttpResponse, IHttpClient } from '@infra/http/IHttpClient'
import axios, { AxiosResponse } from 'axios'
import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { SessionExpired } from '@/domain/events/SessionExpired'
import { AuthState } from '@/domain/states/AuthState'

import { readAuthCookie } from './authCookie'
import { httpAgent } from './axiosInstance'
import { HttpTransportError } from './HttpTransportError'

import { classifyHttpFailure } from '@/utils/withNetworkRetry'
import { withNetworkRetry } from '@/utils/withNetworkRetry'

/**
 * Orçamento de retentativas para falha de TRANSPORTE (flap de rede, timeout).
 *
 * Fica aqui, e não espalhado por repositório, porque conserta os ~19 repositórios, o boot e as
 * chamadas fire-and-forget de uma vez. Três tentativas com jitter cobrem uma janela de ~1s, que é a
 * duração típica de um evento do `NetworkChangeNotifier` do Chrome.
 *
 * Regra de convivência com o resto do app: **transporte retenta aqui; ninguém mais retenta erro já
 * marcado `isTransient`.** Empilhar dois mecanismos multiplica requisições e piora o problema.
 */
const TRANSIENT_RETRY = { retries: 2, baseDelayMs: 250, maxDelayMs: 1000 }

/** Statuses que significam "a sessão pode ter morrido". A API do ecossistema usa 401 e 403. */
const SESSION_FAILURE_STATUSES = [401, 403]

@injectable()
export class AxiosHttpClient implements IHttpClient {
  constructor(@inject(InjectionTokens.AuthState) private authState: AuthState) {}

  async get<T>(url: string, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>((requestOptions) => httpAgent.get<T>(url, requestOptions), options, true)
  }

  async post<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.post<T>(url, body, requestOptions),
      options,
      options?.idempotent === true
    )
  }

  async put<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.put(url, body, requestOptions),
      options,
      options?.idempotent === true
    )
  }

  async delete<T>(url: string, body: HttpBody, options?: HttpOptions): Promise<HttpResponse<T>> {
    return this._send<T>(
      (requestOptions) => httpAgent.delete(url, { ...requestOptions, data: body }),
      options,
      options?.idempotent === true
    )
  }

  // PRIVATE METHODS

  /**
   * Executa a requisição e, num 401, tenta recuperar a sessão uma única vez.
   *
   * O token vive em `AuthState`, preenchido a partir do cookie `_nat` no boot. Como o cookie é de
   * domínio compartilhado com o app legado, ele pode rotacionar enquanto a aba está aberta — a aba
   * seguia usando o token morto e toda escrita passava a falhar com 401 até um reload, sem nenhuma
   * pista para o usuário. Aqui a gente relê o cookie: se ele mudou, atualiza o estado e repete a
   * requisição com o token novo; se não mudou, a sessão realmente acabou e o evento avisa quem trata
   * a UX (Authenticator).
   */
  private async _send<T>(
    perform: (options: HttpOptions & { validateStatus: () => boolean }) => Promise<AxiosResponse<T>>,
    options?: HttpOptions,
    allowTransientRetry = false,
    isSessionRetry = false
  ): Promise<HttpResponse<T>> {
    // `idempotent` é nosso, não do axios: não deve viajar na config da requisição.
    const { idempotent: _idempotent, ...axiosOptions } = options ?? {}

    try {
      const requestOptions = { ...axiosOptions, validateStatus: () => true }

      // `validateStatus: () => true` faz com que NENHUM status HTTP rejeite. Logo `perform` só
      // rejeita por falha de transporte — que é exatamente o que vale retentar. Um 5xx é resposta
      // do servidor e passa direto, sem retry (decisão de quem chama).
      const axiosResponse = allowTransientRetry
        ? await withNetworkRetry(() => perform(requestOptions), TRANSIENT_RETRY)
        : await perform(requestOptions)

      // 403 entra junto com 401 porque a API do ecossistema responde 403 para sessão morta. Não há
      // risco de confundir com "sem permissão" nem com o 403 do DigitalOcean Spaces (manifest de
      // cartas ausente): `_recoverSession` só age quando a requisição enviou `Authorization`, e
      // essas chamadas não enviam.
      if (SESSION_FAILURE_STATUSES.includes(axiosResponse.status) && !isSessionRetry) {
        const recoveredOptions = this._recoverSession(options)
        if (recoveredOptions) {
          return this._send<T>(perform, recoveredOptions, allowTransientRetry, true)
        }
      }

      const plainHeaders = Object.fromEntries(axiosResponse.headers as any)

      return {
        success: true,
        status: axiosResponse.status,
        headers: plainHeaders,
        response: axiosResponse.data
      }
    } catch (error: any) {
      const classification = classifyHttpFailure(error)

      return {
        success: false,
        status: error?.response?.status,
        error: new HttpTransportError(error?.message ?? 'Network error', {
          code: error?.code,
          httpStatus: error?.response?.status,
          originalError: error
        }),
        code: error?.code,
        isTransient: classification === 'transient',
        isCanceled: classification === 'canceled'
      }
    }
  }

  /**
   * Devolve as options com o token novo quando o cookie tem um token diferente do que foi enviado.
   * Devolve `null` quando não há como recuperar — aí emite `SessionExpired`.
   */
  private _recoverSession(options?: HttpOptions): HttpOptions | null {
    const sentAuthorization = this._getAuthorizationHeader(options)
    // Requisição sem Authorization (ex.: serviço de terceiro) não é problema de sessão nossa.
    if (!sentAuthorization) return null

    const cookieToken = readAuthCookie()
    if (!cookieToken) {
      SessionExpired.next()
      return null
    }

    const freshAuthorization = `Token ${cookieToken}`
    if (freshAuthorization === sentAuthorization) {
      // O cookie continua com o mesmo token que o servidor acabou de rejeitar: sessão encerrada.
      SessionExpired.next()
      return null
    }

    if (cookieToken !== this.authState.getStateSnapshot().token) {
      this.authState.setToken(cookieToken)
    }

    const headers = { ...(options?.headers ?? {}) } as Record<string, string>
    const headerKey = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization') ?? 'Authorization'
    headers[headerKey] = freshAuthorization

    return { ...options, headers }
  }

  private _getAuthorizationHeader(options?: HttpOptions): string | null {
    const headers = (options?.headers ?? {}) as Record<string, unknown>
    const headerKey = Object.keys(headers).find((key) => key.toLowerCase() === 'authorization')
    if (!headerKey) return null

    const value = headers[headerKey]
    return typeof value === 'string' ? value : null
  }
}
